<template>
  <MainLayout>
    <PageHeader>
      <template #pageTitle>Hello, {{ userStore.getFirstName() }}</template>
      <template #pageDescription></template>
    </PageHeader>

    <div class="overview-overline desktop-only">
      <div class="overline--headline">Overview</div>
      <div class="overline--controls">
        <Button
          :link="`/event-compare?event_id=${analyticsStore.chosenEventId}`"
          severity="secondary"
        >
          <template #iconLeft><IconCompare /></template>
          Compare Events
        </Button>
        <FeatureFlag name="vantage_echo" v-if="shouldShowEcho">
          <Button
            severity="secondary"
            :link="`/echo?eventId=${analyticsStore.chosenEventId}`"
            :showNewFeatureBadge="true"
          >
            <template #iconLeft><IconEcho /></template>
            Echo
          </Button>
        </FeatureFlag>
        <FeatureFlag name="premium_benchmarking">
          <Button severity="primary" :link="`/benchmarking?eventId=${analyticsStore.chosenEventId}`">
            <template #iconLeft>
              <Tooltip position="top">
                <template v-slot:text>
                  <IconPremium class="premium-icon" />
                </template>
                <template v-slot:tooltip> Premium Feature </template>
              </Tooltip>
            </template>
            Benchmark Events
          </Button>
        </FeatureFlag>

        <Tooltip position="left" :hasTooltip="!analyticsStore.chosenEventDetails?.event_completed">
          <template v-slot:text>
            <Button
              :disabled="
                !analyticsStore.chosenEvent || !analyticsStore.chosenEventDetails?.event_completed
              "
              severity="primary"
              @click="downloadReport"
            >
              <template #iconLeft><IconDownload /></template>
              <span class="desktop-only">Download Report</span>
            </Button>
          </template>
          <template v-slot:tooltip> This event has not completed yet. </template>
        </Tooltip>
      </div>
    </div>

    <LoadingSpinner v-if="analyticsStore.loading" />

    <OverviewTopCard />

    <template v-if="!analyticsStore.loading && analyticsStore.chosenEvent">
      <OverviewNotLivePlaceholder 
        v-if="isFutureEvent" 
        type="event" 
        :eventId="analyticsStore.chosenEvent.id" 
      />
      <AnalyticsDashboard v-else ref="analyticsDashboardRef" />
    </template>
  </MainLayout>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { getEventReport } from '@/utils/api';
import { useEventsStore } from '@/stores/events';
import { useAnalyticsStore } from '@/stores/analytics';
import { useUserStore } from '@/stores/user';
import { useToastStore } from '@/stores/toast';
import mixpanel from '@/mixpanel.ts';

import Button from '@/components/global/Button.vue';
import MainLayout from '@/components/global/MainLayout.vue';
import LoadingSpinner from '@/components/global/LoadingSpinner.vue';
import PageHeader from '@/components/global/PageHeader.vue';
import Tooltip from '@/components/global/Tooltip.vue';
import OverviewTopCard from '@/components/overview/TopCard.vue';
import AnalyticsDashboard from '@/components/analytics/AnalyticsDashboard.vue';
import FeatureFlag from '@/components/global/FeatureFlag.vue';
import OverviewNotLivePlaceholder from '@/components/overview/NotLivePlaceholder.vue';
import IconCompare from '@/icons/analytics/IconCompare.svg?component';
import IconDownload from '@/icons/analytics/IconDownload.svg?component';
import IconPremium from '@/icons/IconPremium.svg?component';
import IconEcho from '@/icons/IconEcho.svg?component';

const eventsStore = useEventsStore();
const analyticsStore = useAnalyticsStore();
const userStore = useUserStore();
const toast = useToastStore();

eventsStore.getEventListData();

const isFutureEvent = computed(() => {
  if (analyticsStore.chosenEventDetails) {
    return !analyticsStore.chosenEventDetails.event_started;
  }
  return false;
});

const shouldShowEcho = computed(() => {
  return analyticsStore.chosenEventDetails?.id && analyticsStore.chosenEventDetails?.event_completed && analyticsStore.chosenEventDetails?.zones?.length && analyticsStore.chosenEventDetails?.zones?.length > 2;
});

const downloadReport = () => {
  if (analyticsStore.chosenEvent?.id) {
    getEventReport(userStore.accessToken, { event_id: analyticsStore.chosenEventId }).then(
      (resp) => {
        const fileURL = window.URL.createObjectURL(new Blob([resp.data]));
        const fileLink = document.createElement('a');
        fileLink.href = fileURL;
        fileLink.setAttribute('download', `${analyticsStore.chosenEvent?.name}.xlsx`);
        document.body.appendChild(fileLink);
        fileLink.click();
      },
      () => {
        toast.manual('error', 'Report could not be downloaded. Please contact Meshh Support.');
      },
    );
    mixpanel.track('Download Full Event Report', {
      'Event Name': analyticsStore.chosenEventDetails?.name,
      'Event UUID': analyticsStore.chosenEventDetails?.uuid,
      'Event ID': analyticsStore.chosenEventDetails?.id,
    });
  }
};
</script>

<style lang="scss">
.desktop-only {
  display: none;

  @media screen and (min-width: 768px) {
    display: inline;
  }
}

.widget .apexcharts-tooltip.apexcharts-theme-light {
  background: variables.$primitives-color-tertiary-500;
  border: 1px solid variables.$primitives-color-border-200;
  color: variables.$primitives-color-background-100;

  .apexcharts-tooltip-title {
    background-color: transparent;
    border: none;
    margin-bottom: 0;
  }
}

.overview-overline {
  margin-bottom: variables.$sizing-sm;

  @media screen and (min-width: 768px) {
    @include mixins.flex-center($horiz: space-between);
  }

  .overline--headline {
    @include typography.font-display($size: xs, $weight: medium);
    color: variables.$primitives-color-tertiary-500;
  }

  .overline--controls {
    display: flex;
    flex-direction: column;
    gap: variables.$sizing-xs;
    margin-left: auto;

    @media screen and (min-width: 768px) {
      @include mixins.flex-center($gap: variables.$sizing-xs);
      flex-direction: row;
    }

    svg {
      height: variables.$sizing-md;
      min-height: variables.$sizing-md;
      min-width: variables.$sizing-md;
      width: variables.$sizing-md;
    }

    .vue-tooltip {
      display: block;

      @media screen and (min-width: 768px) {
        display: flex;
      }

      button {
        width: 100%;
      }
    }
  }
}

.grid-stack {
  @media screen and (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: variables.$sizing-sm;
    margin-top: variables.$sizing-sm;
  }

  > div {
    @media screen and (max-width: 768px) {
      height: auto !important;
      left: 0 !important;
      position: relative !important;
      top: 0 !important;
      width: 100% !important;
      /* NOTE: !important is required to override the grid-stack plugin. all of the widgets on mobile are the same size, regardless of the classes added to them */
    }
  }
}
</style>
